import { useSyncExternalStore } from 'react'

// The albums and recipe books you opened last in a family, newest first, for the sidebar's "Recent". Kept on this
// device only: it's where you've been, not something the circle shares.
export type Visit = { to: string; title: string; kind: 'album' | 'book' }

const MAX = 5
const key = (familyId: string) => `gatheredfrom:recent:${familyId}`
const cache = new Map<string, Visit[]>()
const listeners = new Set<() => void>()
const EMPTY: Visit[] = []

const isVisit = (x: unknown): x is Visit =>
  !!x &&
  typeof (x as Visit).to === 'string' &&
  typeof (x as Visit).title === 'string' &&
  ((x as Visit).kind === 'album' || (x as Visit).kind === 'book')

function read(familyId: string): Visit[] {
  if (!cache.has(familyId)) {
    let visits: Visit[] = []
    try {
      const parsed: unknown = JSON.parse(localStorage.getItem(key(familyId)) ?? '[]')
      if (Array.isArray(parsed)) visits = parsed.filter(isVisit).slice(0, MAX)
    } catch {
      // Unreadable or refused storage: start empty.
    }
    cache.set(familyId, visits)
  }
  return cache.get(familyId)!
}

// Puts a visit first, dropping any earlier one to the same place.
export function withVisit(visits: Visit[], visit: Visit): Visit[] {
  return [visit, ...visits.filter((x) => x.to !== visit.to)].slice(0, MAX)
}

export function rememberVisit(familyId: string, visit: Visit) {
  const before = read(familyId)
  if (before[0]?.to === visit.to && before[0].title === visit.title) return
  const next = withVisit(before, visit)
  cache.set(familyId, next)
  try {
    localStorage.setItem(key(familyId), JSON.stringify(next))
  } catch {
    // Remembered until the page reloads.
  }
  listeners.forEach((listener) => listener())
}

// Forgets a place that's gone (an album or book deleted, or no longer yours to see).
export function forgetVisit(familyId: string, to: string) {
  const before = read(familyId)
  if (!before.some((x) => x.to === to)) return
  const next = before.filter((x) => x.to !== to)
  cache.set(familyId, next)
  try {
    localStorage.setItem(key(familyId), JSON.stringify(next))
  } catch {
    // Nothing more to do.
  }
  listeners.forEach((listener) => listener())
}

const subscribe = (listener: () => void) => {
  listeners.add(listener)
  return () => listeners.delete(listener)
}

export const useRecent = (familyId: string | null) =>
  useSyncExternalStore(subscribe, () => (familyId ? read(familyId) : EMPTY))
